import { AppEventTypes } from '@buxus/shared'
import { trackEvent } from './webAnalytics'

/** Set after first `app_started` in this tab; cleared on logout. */
const APP_STARTED_SENT_KEY = 'buxus.web.analytics.app_started_sent'

let lastPageViewKey: string | null = null
let pageViewDebounceTimer: ReturnType<typeof setTimeout> | null = null

/** Clear dedupe state on logout so the next session can emit fresh lifecycle events. */
export function resetAnalyticsSessionTracking(): void {
  lastPageViewKey = null
  if (pageViewDebounceTimer !== null) {
    clearTimeout(pageViewDebounceTimer)
    pageViewDebounceTimer = null
  }
  try {
    sessionStorage.removeItem(APP_STARTED_SENT_KEY)
  } catch {
    /* private mode */
  }
}

/** @deprecated Use {@link resetAnalyticsSessionTracking}. */
export const resetPageViewTracking = resetAnalyticsSessionTracking

/**
 * Emit at most one `app_started` per tab until logout. Skips OIDC callback loads and
 * other full-page reloads in the same signed-in visit.
 */
export function trackAppStartedIfNeeded(): void {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.replace(/\/+$/, '') || '/'
    if (path === '/auth/callback') return
  }
  try {
    if (sessionStorage.getItem(APP_STARTED_SENT_KEY) === '1') return
    sessionStorage.setItem(APP_STARTED_SENT_KEY, '1')
  } catch {
    /* private mode — still fire */
  }
  trackEvent(AppEventTypes.APP_STARTED)
}

const PAGE_VIEW_DEBOUNCE_MS = 50

/**
 * Debounce rapid route changes (e.g. `/` → `/home` after OIDC) into one `page_viewed`.
 */
export function trackPageViewIfChanged(
  key: string,
  properties: Record<string, unknown>,
): void {
  if (pageViewDebounceTimer !== null) clearTimeout(pageViewDebounceTimer)
  pageViewDebounceTimer = setTimeout(() => {
    pageViewDebounceTimer = null
    if (key === lastPageViewKey) return
    lastPageViewKey = key
    trackEvent(AppEventTypes.PAGE_VIEWED, properties)
  }, PAGE_VIEW_DEBOUNCE_MS)
}
